// deletion-status.ts — (tf3-34b; Part B §5, §7.5) a data-deletion request's status, in words, for the public page Meta's
// callback links to (/data-deletion/:code). The package's read: { confirmationCode, platform, status: received | done |
// no_account | failed, channelsErased, detail, receivedAt, doneAt }. Pure.
export interface DeletionStatus {
  confirmationCode: string; platform: string; status: "received" | "done" | "no_account" | "failed" | string;
  channelsErased: number; detail: string | null; receivedAt: string; doneAt: string | null;
}
const PLATFORM: Record<string, string> = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube" };

/** Pure: the headline and the line beneath it for a request's status. */
export function deletionWords(s: DeletionStatus): { headline: string; line: string } {
  const p = PLATFORM[s.platform] ?? s.platform;
  switch (s.status) {
    case "done": return { headline: "Your data has been deleted",
      line: `We deleted what we kept from your ${p} account${s.channelsErased > 1 ? `s (${s.channelsErased})` : ""}: the connection, its tokens and its numbers.` };
    case "no_account": return { headline: "We hold nothing for you", line: `No ${p} account of yours was connected to TubeForge, so there was nothing to delete.` };
    case "received": return { headline: "We're deleting your data", line: "Your request is received and is being carried out. Check this page again shortly." };
    case "failed": return { headline: "We couldn't finish yet", line: "Part of the deletion couldn't be done yet. We try again; if this page still says so tomorrow, email privacy@tubeforge.io with your code." };
    default: return { headline: "Your request", line: `Its status is "${s.status}".` };
  }
}
export const UNKNOWN_CODE = "We have no deletion request with this code. Check the link Instagram showed you, or email privacy@tubeforge.io.";

/** (B120-1; D3) Pure: the status line the page shows, in Meta's words: Received / In progress / Completed on <date> / No data
 *  found for this request. A request whose erase hasn't finished (failed: part of it is still to do) is in progress. */
export function statusLabel(s: Pick<DeletionStatus, "status" | "doneAt">): string {
  switch (s.status) {
    case "received": return "Received";
    case "failed": return "In progress";
    case "done": return s.doneAt ? `Completed on ${new Date(s.doneAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })}` : "Completed";
    case "no_account": return "No data found for this request";
    default: return s.status;
  }
}
